import React, { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { createAction } from "./api/actions.api.js";
import { listAdminUsers } from "./api/admin.api";
import FormWithValidation from "./components/common/FormWithValidation";
import { useActionCategoryOptions } from "./hooks/useActionCategoryOptions";
import { Input, Select, Textarea } from "./ui";

const schema = z.object({
  title: z.string().min(1, "Action title is required."),
  description: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  owner: z.string().min(1, "Owner is required."),
  assignee: z.string().min(1, "Assignee is required."),
  status: z.string().optional().nullable(),
  priority: z.string().optional().nullable(),
  due_date: z.string().optional().nullable(),
  source_ref: z.string().optional().nullable(),
  evidence_links: z.string().optional().nullable(),
  parent_action_id: z.string().optional().nullable(),
  control_id: z.string().optional().nullable(),
});

export default function ActionForm({
  onSave,
  onCancel,
  defaultOwner,
  defaultControlId = null,
  defaultParentActionId = null,
  actions = [],
}) {
  const [formError, setFormError] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const categoryOptions = useActionCategoryOptions();
  const normalizeEnum = (value) => {
    if (!value) return null;
    return String(value).trim().toUpperCase().replace(/\s+/g, "_");
  };

  const defaultValues = useMemo(
    () => ({
      title: "",
      description: "",
      category: "",
      owner: defaultOwner || "",
      assignee: "",
      status: "OPEN",
      priority: "",
      due_date: "",
      source_ref: "",
      evidence_links: "",
      parent_action_id: defaultParentActionId ? String(defaultParentActionId) : "",
      control_id: defaultControlId ? String(defaultControlId) : "",
    }),
    [defaultOwner, defaultParentActionId, defaultControlId]
  );

  useEffect(() => {
    let active = true;
    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const res = await listAdminUsers();
        const users = res?.data || [];
        const options = users
          .filter((u) => u?.username)
          .map((u) => ({
            value: u.username,
            label: u.email ? `${u.username} (${u.email})` : u.username,
          }));
        if (active) {
          setUserOptions(options);
        }
      } catch (err) {
        if (active) {
          setUserOptions([]);
          setUsersError(err?.response?.data?.detail || err?.message || "Unable to load users.");
        }
      } finally {
        if (active) {
          setUsersLoading(false);
        }
      }
    };
    loadUsers();
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (values, methods) => {
    setFormError(null);
    const parentId = values.parent_action_id ? Number(values.parent_action_id) : null;
    const ctrlId = values.control_id ? Number(values.control_id) : null;
    const dueDateIso = values.due_date ? new Date(`${values.due_date}T00:00:00Z`).toISOString() : null;
    const payload = {
      title: values.title.trim(),
      description: values.description?.trim() || null,
      category: values.category?.trim() || null,
      owner: values.owner?.trim() || null,
      assignee: values.assignee?.trim() || null,
      status: normalizeEnum(values.status) || "OPEN",
      priority: normalizeEnum(values.priority),
      due_date: dueDateIso,
      source_ref: values.source_ref?.trim() || null,
      evidence_links: values.evidence_links
        ? values.evidence_links.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      parent_action_id: parentId,
      control_id: ctrlId,
    };

    try {
      if (onSave) {
        const saved = await onSave(payload);
        if (saved === false) return;
      } else {
        await createAction(payload);
      }
      methods?.reset?.(defaultValues);
    } catch (err) {
      setFormError(err?.message || "Failed to save action");
    }
  };

  return (
    <FormWithValidation
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitLabel="Save Action"
      formError={formError}
    >
      {({ register, formState }) => (
        <div style={{ marginBottom: "10px" }}>
          <Input
            id="action-title"
            type="text"
            ariaLabel="Action title"
            placeholder="Action title"
            style={{ width: "100%", padding: "8px", marginBottom: "6px" }}
            error={formState.errors.title?.message}
            {...register("title")}
          />
          <Select
            id="action-category"
            ariaLabel="Category"
            style={{ width: "100%", padding: "8px", marginBottom: "6px" }}
            {...register("category")}
          >
            <option value="">Select category</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Textarea
            ariaLabel="Description"
            placeholder="Description"
            style={{ width: "100%", padding: "8px", marginBottom: "6px", minHeight: "60px" }}
            {...register("description")}
          />
          <Select
            id="action-owner"
            ariaLabel="Owner"
            style={{ width: "100%", padding: "8px", marginBottom: "6px" }}
            disabled={usersLoading || userOptions.length === 0}
            {...register("owner")}
          >
            <option value="">Select owner</option>
            {userOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Select
            id="action-assignee"
            ariaLabel="Assignee"
            style={{ width: "100%", padding: "8px", marginBottom: "6px" }}
            disabled={usersLoading || userOptions.length === 0}
            {...register("assignee")}
          >
            <option value="">Select assignee</option>
            {userOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          {usersError && userOptions.length === 0 && (
            <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: "6px" }}>{usersError}</div>
          )}
          <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
            <Select
              id="action-status"
              ariaLabel="Status"
              style={{ flex: 1, padding: "8px" }}
              {...register("status")}
            >
            {[
              { value: "OPEN", label: "Open" },
              { value: "IN_PROGRESS", label: "In Progress" },
              { value: "BLOCKED", label: "Blocked" },
              { value: "COMPLETED", label: "Completed" },
            ].map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
            <Select
              id="action-priority"
              ariaLabel="Priority"
              style={{ flex: 1, padding: "8px" }}
              {...register("priority")}
            >
              <option value="">Priority (optional)</option>
              {[
                { value: "LOW", label: "Low" },
                { value: "MEDIUM", label: "Medium" },
                { value: "HIGH", label: "High" },
                { value: "CRITICAL", label: "Critical" },
              ].map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
            <Input
              id="action-due-date"
              type="date"
              ariaLabel="Due date"
              style={{ flex: 1, padding: "8px" }}
              {...register("due_date")}
            />
            <Input
              id="action-source-ref"
              type="text"
              ariaLabel="Source reference"
              placeholder="Source reference"
              style={{ flex: 1, padding: "8px" }}
              {...register("source_ref")}
            />
          </div>
          <Input
            id="action-evidence-links"
            type="text"
            ariaLabel="Evidence links"
            placeholder="Evidence links (comma separated URLs)"
            style={{ width: "100%", padding: "8px", marginBottom: "6px" }}
            {...register("evidence_links")}
          />
          <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
            <Select
              id="action-parent-id"
              ariaLabel="Parent action"
              style={{ flex: 1, padding: "8px" }}
              {...register("parent_action_id")}
            >
              <option value="">No parent action</option>
              {actions.map((a) => (
                <option key={a.id} value={a.id}>
                  #{a.id} - {a.title || a.name}
                </option>
              ))}
            </Select>
            <Input
              id="action-control-id"
              type="number"
              ariaLabel="Control ID"
              placeholder="Control ID (optional)"
              style={{ flex: 1, padding: "8px" }}
              {...register("control_id")}
            />
          </div>
        </div>
      )}
    </FormWithValidation>
  );
}
